import Input from "@/components/Input";
import Select from "@/components/Select";
import * as React from "react";
import { FormGroup } from "../Workflows/RightAside/RightAsideHelpers";
import {
  Invitation,
  invitationTypeDefs,
  InvitationResetFrequencies,
  InvitationAnimations
} from "./types";
import { FormValidator } from "@velaro/velaro-component-lib";

interface Props {
  onUpdate(invitation: Invitation): void;
  invitation: Invitation;
  errors: Record<string, string>;
}

export default function BaseInvitationEditor({
  invitation,
  errors,
  onUpdate
}: Props) {
  return (
    <>
      <FormGroup label="Type">
        <FormValidator error={errors["type"]}>
          <Select
            options={invitationTypeDefs}
            value={invitation.type}
            onChange={(type) => onUpdate({ ...invitation, type })}
          />
        </FormValidator>
      </FormGroup>
      <FormGroup label="Name">
        <FormValidator error={errors["name"]}>
          <Input
            value={invitation.name}
            onChange={(name) => onUpdate({ ...invitation, name })}
          />
        </FormValidator>
      </FormGroup>
      <FormGroup label="Description">
        <Input
          value={invitation.description}
          onChange={(description) => onUpdate({ ...invitation, description })}
        />
      </FormGroup>
      <FormGroup label="Reset Frequency" tooltipText="How often this invitation resets — visitor sees it again after this period">
        <Select
          options={InvitationResetFrequencies.map((value) => ({
            label: value,
            value
          }))}
          value={invitation.resetFrequency}
          onChange={(resetFrequency) =>
            onUpdate({ ...invitation, resetFrequency })
          }
        />
      </FormGroup>
      <FormGroup label="Max Fires" tooltipText="Maximum times to show per visitor per reset period. Leave empty for unlimited.">
        <Input
          type="number"
          value={invitation.maxFires !== undefined ? String(invitation.maxFires) : ""}
          onChange={(value) =>
            onUpdate({
              ...invitation,
              maxFires: value !== "" ? parseInt(value) || 1 : undefined
            })
          }
        />
      </FormGroup>
      <FormGroup label="Entry Animation" tooltipText="Which edge the invitation slides in from">
        <Select
          options={InvitationAnimations.map((value) => ({
            label: value
              .split("-")
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" "),
            value
          }))}
          value={invitation.startingAnimation ?? "right-center"}
          onChange={(startingAnimation) =>
            onUpdate({ ...invitation, startingAnimation })
          }
        />
      </FormGroup>
      <div className="flex gap-4">
        <FormGroup label="Float in" tooltipText="Animate the invitation sliding onto the screen">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={invitation.floatOntoScreen ?? true}
              onChange={(e) =>
                onUpdate({ ...invitation, floatOntoScreen: e.target.checked })
              }
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-slate-700">Float onto screen</span>
          </label>
        </FormGroup>
        <FormGroup label="Active chats" tooltipText="Show this invitation even when visitor is already in a chat">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={invitation.allowDuringChat ?? false}
              onChange={(e) =>
                onUpdate({ ...invitation, allowDuringChat: e.target.checked })
              }
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-slate-700">Allow during chat</span>
          </label>
        </FormGroup>
      </div>
    </>
  );
}
